import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { 
  Activity, 
  Shield, 
  Clock, 
  Users, 
  CheckCircle2, 
  ArrowRight,
  Menu,
  X,
  Workflow,
  Lock,
  Eye,
  Stethoscope,
  BarChart3,
  HeartPulse
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Header = ({ scrollToForm }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: "About", href: "#intro" },
    { label: "Platform", href: "#platform" },
    { label: "Clinical", href: "#clinical" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2" data-testid="logo">
            <span className="font-['Outfit'] text-xl font-semibold text-[#0B183D]">
              Syntra Health
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-[#4B5A7D] hover:text-[#0B183D] transition-colors font-medium"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <button
              onClick={scrollToForm}
              className="btn-primary text-sm"
              data-testid="header-cta-button"
            >
              Request early access
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-[#D8E1EC] px-6 py-4" data-testid="mobile-menu">
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-[#4B5A7D] hover:text-[#0B183D] transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                scrollToForm();
              }}
              className="btn-primary text-sm mt-2"
            >
              Request early access
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = ({ scrollToForm }) => {
  return (
    <section className="min-h-screen flex items-center pt-20" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-4xl">
          <h1 
            className="font-['Outfit'] text-5xl md:text-7xl tracking-tighter leading-[1.1] font-medium text-[#0B183D] opacity-0 animate-fade-in-up"
            style={{ animationFillMode: 'forwards' }}
          >
            Metabolic testing, made simpler
          </h1>
          <p 
            className="mt-8 text-lg md:text-xl leading-relaxed text-[#4B5A7D] max-w-2xl opacity-0 animate-fade-in-up animation-delay-100"
            style={{ animationFillMode: 'forwards' }}
          >
            Syntra Health is building a new model for time-sensitive metabolic testing beyond the clinic—designed to support patients, preserve protocol, and maintain clinical oversight.
          </p>
          <div 
            className="mt-10 flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up animation-delay-200"
            style={{ animationFillMode: 'forwards' }}
          >
            <button
              onClick={scrollToForm}
              className="btn-primary"
              data-testid="hero-cta-button"
            >
              Request early access
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <button
              onClick={scrollToForm}
              className="btn-outline"
              data-testid="hero-secondary-cta"
            >
              Speak to our team
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Intro Section
const IntroSection = () => {
  return (
    <section id="intro" className="py-24 md:py-32 relative grid-pattern" data-testid="intro-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-['Outfit'] text-3xl md:text-5xl tracking-tight leading-tight font-medium text-[#0B183D]">
            Diagnostics should fit real life
          </h2>
          <p className="mt-8 text-lg md:text-xl leading-relaxed text-[#4B5A7D]">
            Many important metabolic tests are difficult to complete under real-world conditions. They depend on timing, preparation, and process. When that process is hard to follow, patients face unnecessary friction and providers lose efficiency.
          </p>
          <p className="mt-6 text-xl md:text-2xl font-medium text-[#0B183D]">
            Syntra is designed to make this easier.
          </p>
        </div>
      </div>
    </section>
  );
};

// What Syntra Does Section
const WhatSyntraDoesSection = () => {
  const features = [
    {
      icon: Activity,
      title: "Accessible Testing",
      description: "Enabling clinically governed metabolic testing beyond the clinic, while preserving the structure these pathways require."
    },
    {
      icon: Clock,
      title: "Easier to Complete",
      description: "Our platform is being developed to make testing easier to access and easier to complete for patients."
    },
    {
      icon: BarChart3,
      title: "Scalable Delivery",
      description: "Designed to help healthcare systems deliver diagnostic pathways at scale without compromising quality."
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-surface" data-testid="what-syntra-does-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#0B183D] mb-4">
            What We Do
          </p>
          <h2 className="font-['Outfit'] text-3xl md:text-5xl tracking-tight leading-tight font-medium text-[#0B183D]">
            A more accessible approach to<br />time-sensitive testing
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="card-flat" data-testid={`feature-card-${index}`}>
              <div className="w-12 h-12 rounded-2xl bg-[#D8E1EC] flex items-center justify-center mb-6">
                <feature.icon className="w-6 h-6 text-[#0B183D]" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-medium text-[#0B183D] mb-3">
                {feature.title}
              </h3>
              <p className="text-[#4B5A7D] leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// For Health Systems Section
const ForHealthSystemsSection = () => {
  const benefits = [
    "Fewer avoidable in-clinic steps",
    "Better pathway visibility",
    "Lower operational friction",
    "Greater confidence in protocol-driven testing"
  ];

  return (
    <section className="py-24 md:py-32" data-testid="health-systems-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#0B183D] mb-4">
              For Health Systems
            </p>
            <h2 className="font-['Outfit'] text-3xl md:text-5xl tracking-tight leading-tight font-medium text-[#0B183D] mb-6">
              Less friction.<br />More confidence.
            </h2>
            <p className="text-lg text-[#4B5A7D] leading-relaxed mb-8">
              Syntra is designed to help providers deliver diagnostic pathways in a way that is simpler for patients and more manageable for care teams.
            </p>
            <ul className="space-y-4">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-3" data-testid={`health-system-benefit-${index}`}>
                  <CheckCircle2 className="w-5 h-5 text-[#0B183D] flex-shrink-0" />
                  <span className="text-[#0B183D] font-medium">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Visual Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#F5F8FC] rounded-2xl p-6 flex flex-col items-center justify-center text-center">
              <Shield className="w-8 h-8 text-[#0B183D] mb-3" />
              <p className="text-sm font-medium text-[#0B183D]">Protocol Compliance</p>
            </div>
            <div className="bg-[#F5F8FC] rounded-2xl p-6 flex flex-col items-center justify-center text-center">
              <Eye className="w-8 h-8 text-[#0B183D] mb-3" />
              <p className="text-sm font-medium text-[#0B183D]">Full Visibility</p>
            </div>
            <div className="bg-[#F5F8FC] rounded-2xl p-6 flex flex-col items-center justify-center text-center">
              <Clock className="w-8 h-8 text-[#0B183D] mb-3" />
              <p className="text-sm font-medium text-[#0B183D]">Time Savings</p>
            </div>
            <div className="bg-[#F5F8FC] rounded-2xl p-6 flex flex-col items-center justify-center text-center">
              <Activity className="w-8 h-8 text-[#0B183D] mb-3" />
              <p className="text-sm font-medium text-[#0B183D]">Better Outcomes</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// For Patients Section
const ForPatientsSection = () => {
  return (
    <section className="py-24 md:py-32 bg-surface" data-testid="patients-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Abstract Mobile Visual */}
          <div className="relative order-2 lg:order-1">
            <div className="max-w-xs mx-auto">
              {/* Phone frame */}
              <div className="bg-[#0B183D] rounded-[2.5rem] p-3">
                <div className="bg-white rounded-[2rem] p-6 min-h-[400px]">
                  {/* App header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="h-2 bg-[#D8E1EC] rounded-full w-20"></div>
                    <div className="w-8 h-8 rounded-full bg-[#F5F8FC]"></div>
                  </div>
                  {/* Progress indicator */}
                  <div className="mb-6">
                    <div className="flex gap-2 mb-2">
                      {[1, 2, 3, 4].map((i) => (
                        <div 
                          key={i} 
                          className={`flex-1 h-1.5 rounded-full ${i <= 2 ? 'bg-[#0B183D]' : 'bg-[#D8E1EC]'}`}
                        ></div>
                      ))}
                    </div>
                    <div className="h-2 bg-[#D8E1EC] rounded-full w-1/3"></div>
                  </div>
                  {/* Content blocks */}
                  <div className="space-y-4">
                    <div className="bg-[#F5F8FC] rounded-2xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-[#D8E1EC] flex items-center justify-center">
                          <Clock className="w-5 h-5 text-[#0B183D]" />
                        </div>
                        <div>
                          <div className="h-2 bg-[#0B183D] rounded-full w-16 mb-1"></div>
                          <div className="h-1.5 bg-[#D8E1EC] rounded-full w-24"></div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-[#F5F8FC] rounded-2xl p-4">
                      <div className="h-2 bg-[#D8E1EC] rounded-full w-full mb-2"></div>
                      <div className="h-2 bg-[#D8E1EC] rounded-full w-2/3"></div>
                    </div>
                    <button className="w-full bg-[#0B183D] text-white rounded-xl py-3 text-sm font-medium">
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Text Content */}
          <div className="order-1 lg:order-2">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#0B183D] mb-4">
              For Patients
            </p>
            <h2 className="font-['Outfit'] text-3xl md:text-5xl tracking-tight leading-tight font-medium text-[#0B183D] mb-6">
              Designed around the patient
            </h2>
            <p className="text-lg text-[#4B5A7D] leading-relaxed mb-6">
              Testing should be clear, manageable, and easier to complete.
            </p>
            <p className="text-lg text-[#4B5A7D] leading-relaxed">
              Syntra is being built to reduce disruption and support the patient through the process.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Platform Overview Section
const PlatformSection = () => {
  const capabilities = [
    {
      icon: Workflow,
      title: "Guided workflow",
      description: "Supports the patient through each stage of the testing process.",
      size: "normal"
    },
    {
      icon: Clock,
      title: "Protocol-aware design",
      description: "Built to help ensure that key steps happen in the right sequence and at the right time.",
      size: "large"
    },
    {
      icon: Activity,
      title: "Connected measurement",
      description: "Captures data within a structured digital pathway.",
      size: "normal"
    },
    {
      icon: Lock,
      title: "Secure traceability",
      description: "Maintains a clear, auditable flow of information from patient to clinician.",
      size: "normal"
    },
    {
      icon: Eye,
      title: "Clinical oversight",
      description: "Keeps healthcare professionals in control of review and decision-making.",
      size: "large"
    }
  ];

  return (
    <section id="platform" className="py-24 md:py-32" data-testid="platform-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#0B183D] mb-4">
            Platform Overview
          </p>
          <h2 className="font-['Outfit'] text-3xl md:text-5xl tracking-tight leading-tight font-medium text-[#0B183D] mb-4">
            A platform designed for<br />protocol-driven testing
          </h2>
          <p className="text-lg text-[#4B5A7D]">
            Connected. Guided. Clinically governed.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {capabilities.map((cap, index) => (
            <div 
              key={index} 
              className={`bento-item ${cap.size === 'large' ? 'col-span-2 md:col-span-1' : ''}`}
              data-testid={`platform-capability-${index}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-[#D8E1EC] flex items-center justify-center mb-4">
                <cap.icon className="w-6 h-6 text-[#0B183D]" />
              </div>
              <h3 className="font-['Outfit'] text-lg md:text-xl font-medium text-[#0B183D] mb-2">
                {cap.title}
              </h3>
              <p className="text-sm md:text-base text-[#4B5A7D] leading-relaxed">
                {cap.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-lg text-[#4B5A7D] max-w-2xl mx-auto">
            Too many diagnostic pathways remain difficult to complete and difficult to scale. 
            <span className="text-[#0B183D] font-medium"> Syntra is designed to make the pathway itself more reliable.</span>
          </p>
        </div>
      </div>
    </section>
  );
};

// Clinical/Evidence Section
const ClinicalSection = () => {
  return (
    <section id="clinical" className="py-24 md:py-32 bg-[#0B183D]" data-testid="clinical-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#D8E1EC] mb-4">
              Clinical Evidence
            </p>
            <h2 className="font-['Outfit'] text-3xl md:text-5xl tracking-tight leading-tight font-medium text-white mb-6">
              Built around diagnostic integrity
            </h2>
            <p className="text-lg text-[#D8E1EC] leading-relaxed mb-6">
              For time-sensitive metabolic testing, the pathway matters.
            </p>
            <p className="text-lg text-[#D8E1EC] leading-relaxed">
              Variation in preparation, timing, execution, or handling can affect whether a test is completed properly and whether the result is dependable.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-['Outfit'] text-xl font-medium text-white mb-2">
                    Structured workflows
                  </h3>
                  <p className="text-[#D8E1EC]">
                    Designed to reduce variability through guided execution and clear protocols.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <HeartPulse className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-['Outfit'] text-xl font-medium text-white mb-2">
                    Real-world validation
                  </h3>
                  <p className="text-[#D8E1EC]">
                    Working with partners to evaluate usability, pathway performance, and clinical implementation.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-['Outfit'] text-xl font-medium text-white mb-2">
                    Partner collaboration
                  </h3>
                  <p className="text-[#D8E1EC]">
                    Engaging healthcare providers and clinical partners to develop this new model for diagnostic delivery.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Final CTA / Form Section
const CTASection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", message: "", requestType: "early-access" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error("Please fill in required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/early-access`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to submit");
      }
      
      setIsSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
      setFormData({ name: "", email: "", company: "", message: "", requestType: "early-access" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="early-access" className="py-24 md:py-32 bg-surface" data-testid="cta-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-['Outfit'] text-3xl md:text-5xl tracking-tight leading-tight font-medium text-[#0B183D] mb-6">
            A better way to deliver metabolic testing
          </h2>
          <p className="text-lg text-[#4B5A7D] leading-relaxed mb-12">
            Syntra Health is building a new infrastructure for time-sensitive diagnostic care—more accessible for patients and more workable for providers.
          </p>

          {isSubmitted ? (
            <div className="bg-white rounded-3xl p-8 md:p-12 border border-[#D8E1EC]" data-testid="success-message">
              <div className="w-16 h-16 rounded-full bg-[#0B183D] flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-3">
                Thank you for your interest
              </h3>
              <p className="text-[#4B5A7D]">
                We'll be in touch soon with more information about Syntra Health.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 md:p-12 border border-[#D8E1EC] text-left" data-testid="early-access-form">
              <h3 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-6 text-center">
                Request Early Access or Speak To Our Team
              </h3>
              
              {/* Request Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#0B183D] mb-3">
                  I would like to:
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="requestType"
                      value="early-access"
                      checked={formData.requestType === "early-access"}
                      onChange={(e) => setFormData({ ...formData, requestType: e.target.value })}
                      className="w-5 h-5 accent-[#0B183D]"
                      data-testid="radio-early-access"
                    />
                    <span className="text-[#0B183D] group-hover:text-[#162B66] transition-colors">Request early access</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="requestType"
                      value="speak-to-team"
                      checked={formData.requestType === "speak-to-team"}
                      onChange={(e) => setFormData({ ...formData, requestType: e.target.value })}
                      className="w-5 h-5 accent-[#0B183D]"
                      data-testid="radio-speak-to-team"
                    />
                    <span className="text-[#0B183D] group-hover:text-[#162B66] transition-colors">Speak to the team</span>
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0B183D] mb-1.5">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-field"
                    data-testid="form-name-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0B183D] mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="Email address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field"
                    data-testid="form-email-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0B183D] mb-1.5">
                    Company / Institution
                  </label>
                  <input
                    type="text"
                    placeholder="Your organisation"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="input-field"
                    data-testid="form-company-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0B183D] mb-1.5">
                    Message
                  </label>
                  <textarea
                    placeholder="Tell us about your interest in Syntra Health..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="input-field min-h-[120px] resize-none"
                    data-testid="form-message-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="form-submit-button"
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                  {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                </button>
              </div>
              <p className="mt-4 text-sm text-[#4B5A7D] text-center">
                We'll reach out with more information soon.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="py-12 md:py-16 border-t border-[#D8E1EC]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <span className="font-['Outfit'] text-lg font-semibold text-[#0B183D]">
            Syntra Health
          </span>

          <p className="text-[#4B5A7D] text-center">
            Metabolic testing, made simpler
          </p>

          <Link 
            to="/privacy" 
            className="text-[#4B5A7D] hover:text-[#0B183D] transition-colors text-sm"
            data-testid="footer-privacy-link"
          >
            Privacy Policy
          </Link>

          <p className="text-sm text-[#4B5A7D]">
            © {currentYear} Syntra Health. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Privacy Policy Page
const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Simple Header for Privacy Page */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header" data-testid="privacy-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-2" data-testid="privacy-logo">
              <span className="font-['Outfit'] text-xl font-semibold text-[#0B183D]">
                Syntra Health
              </span>
            </Link>
            <Link 
              to="/" 
              className="text-[#0B183D] hover:text-[#162B66] transition-colors font-medium"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Privacy Policy Content */}
      <main className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h1 className="font-['Outfit'] text-4xl md:text-5xl tracking-tight font-medium text-[#0B183D] mb-4">
            Privacy Policy
          </h1>
          <p className="text-[#4B5A7D] mb-12">Last Updated: 20/04/2026</p>

          <div className="prose prose-lg max-w-none text-[#4B5A7D]">
            <section className="mb-10">
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-4">1. Introduction</h2>
              <p className="leading-relaxed">
                Welcome to Syntra Health. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, and safeguard the information you provide when you submit an inquiry through our website contact form.
              </p>
              <p className="leading-relaxed mt-4">
                Please read this policy carefully. If you disagree with its terms, please discontinue use of our site.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-4">2. Information We Collect</h2>
              <p className="leading-relaxed mb-4">
                When you submit our contact form, we may collect the following personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-[#4B5A7D]">
                <li>Name</li>
                <li>Email address</li>
                <li>Company or institution name</li>
                <li>The content of your inquiry or message</li>
              </ul>
              <p className="leading-relaxed mt-4">
                We do not collect sensitive personal information such as Social Security numbers, financial account details, or health information through this form.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-4">3. How We Use Your Information</h2>
              <p className="leading-relaxed mb-4">
                We use the information you submit for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-[#4B5A7D]">
                <li><strong className="text-[#0B183D]">To respond to your inquiry</strong> — to answer your questions and fulfill the purpose for which you contacted us.</li>
                <li><strong className="text-[#0B183D]">For marketing communications</strong> — with your consent where required by law, we may send you information about our products, services, promotions, and updates that may be of interest to you.</li>
                <li><strong className="text-[#0B183D]">To improve our services</strong> — to understand what inquiries we receive and how we can better serve our customers.</li>
                <li><strong className="text-[#0B183D]">To comply with legal obligations</strong> — where required by applicable law or regulation.</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-4">4. Legal Basis for Processing</h2>
              <p className="leading-relaxed mb-4">
                Depending on your location, we process your personal information under one or more of the following bases:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-[#4B5A7D]">
                <li><strong className="text-[#0B183D]">Performance of a contract</strong> — to respond to your inquiry and provide requested services.</li>
                <li><strong className="text-[#0B183D]">Legitimate interests</strong> — to operate and improve our business communications.</li>
                <li><strong className="text-[#0B183D]">Consent</strong> — for marketing communications, where consent is required.</li>
                <li><strong className="text-[#0B183D]">Legal obligation</strong> — to comply with applicable laws.</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-4">5. Data Retention</h2>
              <p className="leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required or permitted by law. When your data is no longer needed, we will securely delete or anonymize it.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-4">6. Sharing Your Information</h2>
              <p className="leading-relaxed mb-4">
                We do not sell, rent, or trade your personal information to third parties. We do not share your data with advertising partners or data brokers.
              </p>
              <p className="leading-relaxed mb-4">
                We may share your information in limited circumstances only, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-[#4B5A7D]">
                <li>With service providers who assist us in operating our website or business (e.g., email hosting, CRM software), who are contractually bound to protect your data.</li>
                <li>If required by law, court order, or governmental authority.</li>
                <li>To protect the rights, property, or safety of Syntra Health, our customers, or others.</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-4">7. Data Security</h2>
              <p className="leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, loss, misuse, or alteration. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-4">8. Your Privacy Rights</h2>
              <p className="leading-relaxed mb-4">
                Depending on your state of residence, you may have certain rights regarding your personal information. These may include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-[#4B5A7D]">
                <li>The right to access the personal information we hold about you.</li>
                <li>The right to correct inaccurate or incomplete information.</li>
                <li>The right to delete your personal information ("right to be forgotten"), subject to certain exceptions.</li>
                <li>The right to opt out of marketing communications at any time.</li>
                <li>The right to non-discrimination for exercising your privacy rights.</li>
              </ul>
              <p className="leading-relaxed mt-4">
                <strong className="text-[#0B183D]">California Residents (CCPA/CPRA):</strong> You have the right to know what personal information we collect and how it is used, the right to delete your data, the right to correct inaccurate data, and the right to opt out of the sale or sharing of personal information. We do not sell or share your personal information.
              </p>
              <p className="leading-relaxed mt-4">
                <strong className="text-[#0B183D]">Virginia, Colorado, Connecticut, and other state residents:</strong> Similar rights may apply to you under your state's privacy laws. Please contact us to exercise your rights.
              </p>
              <p className="leading-relaxed mt-4">
                To exercise any of your rights, please contact us using the details in Section 11 below. We will respond within the timeframe required by applicable law (typically 30–45 days).
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-4">9. Cookies and Tracking</h2>
              <p className="leading-relaxed">
                Our website may use cookies and similar tracking technologies to improve your browsing experience. Please refer to our Cookie Policy for more information, or adjust your browser settings to refuse cookies if you prefer.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-4">10. Children's Privacy</h2>
              <p className="leading-relaxed">
                Our website and services are not directed to children under the age of 13. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately and we will take steps to delete it.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-4">11. Contact Us</h2>
              <p className="leading-relaxed mb-4">
                If you have any questions about this Privacy Policy, wish to exercise your privacy rights, or want to opt out of marketing communications, please contact us at:
              </p>
              <p className="leading-relaxed">
                <strong className="text-[#0B183D]">Syntra Health</strong><br />
                Email: <a href="mailto:info@syntra-health.com" className="text-[#0B183D] hover:underline">info@syntra-health.com</a>
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#0B183D] mb-4">12. Changes to This Policy</h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. We will post the updated policy on this page with a revised "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 border-t border-[#D8E1EC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <span className="font-['Outfit'] text-lg font-semibold text-[#0B183D]">
              Syntra Health
            </span>
            <p className="text-sm text-[#4B5A7D]">
              © {new Date().getFullYear()} Syntra Health. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  const scrollToForm = () => {
    const formSection = document.getElementById("early-access");
    if (formSection) {
      formSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header scrollToForm={scrollToForm} />
      <main>
        <HeroSection scrollToForm={scrollToForm} />
        <IntroSection />
        <WhatSyntraDoesSection />
        <ForHealthSystemsSection />
        <ForPatientsSection />
        <PlatformSection />
        <ClinicalSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
